import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { lessonVoice, type LessonVoicePlayResult } from "@/lib/audio/lessonVoiceController";
import { recordLearningEvent } from "@/lib/learning-engine/eventLogger";

const ENABLED_KEY = "heirwise.lessonVoice.enabled";
const VOLUME_KEY = "heirwise.lessonVoice.volume";

interface LessonVoiceContextValue {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  volume: number;
  setVolume: (v: number) => void;
  playMoment: (momentId: string) => Promise<LessonVoicePlayResult>;
  preload: (momentIds: string[]) => void;
  stop: () => void;
  speakingMomentId: string | null;
}

const LessonVoiceContext = createContext<LessonVoiceContextValue>({
  enabled: true,
  setEnabled: () => {},
  volume: 0.9,
  setVolume: () => {},
  playMoment: async (momentId: string) => ({ momentId, status: "unavailable" }),
  preload: () => {},
  stop: () => {},
  speakingMomentId: null,
});

function readBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch { /* ignore */ }
  return fallback;
}
function readNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, Math.min(1, n));
  } catch { /* ignore */ }
  return fallback;
}

export function LessonVoiceProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(() => readBool(ENABLED_KEY, true));
  const [volume, setVolumeState] = useState<number>(() => readNumber(VOLUME_KEY, 0.45));
  const [speakingMomentId, setSpeakingMomentId] = useState<string | null>(null);

  useEffect(() => { lessonVoice.setEnabled(enabled); }, [enabled]);
  useEffect(() => { lessonVoice.setVolume(volume); }, [volume]);

  useEffect(() => {
    setSpeakingMomentId(lessonVoice.getSpeakingMomentId());
    return lessonVoice.subscribe(() => {
      setSpeakingMomentId(lessonVoice.getSpeakingMomentId());
    });
  }, []);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try { localStorage.setItem(ENABLED_KEY, String(v)); } catch { /* ignore */ }
  }, []);
  const setVolume = useCallback((v: number) => {
    const c = Math.max(0, Math.min(1, v));
    setVolumeState(c);
    try { localStorage.setItem(VOLUME_KEY, String(c)); } catch { /* ignore */ }
  }, []);

  const playMoment = useCallback(async (momentId: string) => {
    const result = await lessonVoice.play(momentId);
    // Adaptive Learning Engine: log successful, approved voice moment
    // playback. Only fires when audio actually plays (not on cache hits,
    // suppressed, or unavailable). Never issues rewards or unlocks.
    if (result.status === "played") {
      void recordLearningEvent({
        eventType: "voice_moment_played",
        metadata: { momentId, backendStatus: result.backendStatus, cached: result.cached ?? false },
      });
    }
    return result;
  }, []);
  const preload = useCallback((momentIds: string[]) => { void lessonVoice.preload(momentIds); }, []);
  const stop = useCallback(() => { lessonVoice.stop(); }, []);

  const value = useMemo<LessonVoiceContextValue>(
    () => ({ enabled, setEnabled, volume, setVolume, playMoment, preload, stop, speakingMomentId }),
    [enabled, setEnabled, volume, setVolume, playMoment, preload, stop, speakingMomentId],
  );

  return <LessonVoiceContext.Provider value={value}>{children}</LessonVoiceContext.Provider>;
}

export function useLessonVoiceContext() { return useContext(LessonVoiceContext); }